import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Handle, Position, useNodes, useReactFlow, type NodeProps } from '@xyflow/react';
import { alpha, type Theme } from '@mui/material/styles';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Stack,
  Tooltip,
  Button,
  Popover,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  PlayArrow as StartIcon,
  Flag as EndIcon,
  Schedule as DelayIcon,
  AltRoute as ConditionIcon,
  CompareArrows as AbTestIcon,
  StickyNote2 as NoteIcon,
} from '@mui/icons-material';
import {
  CHANNEL_META,
  type ChannelNodeData,
  type DelayNodeData,
  type DelayTimeUnit,
  type ConditionMode,
  type ConditionNodeData,
  type AbTestNodeData,
  normalizeChannelNodeData,
  normalizeConditionNodeData,
  normalizeAbTestNodeData,
  normalizeDelayNodeData,
  clampDelayMinutes,
  DELAY_MIN_MINUTES,
  DELAY_MAX_MINUTES,
  type NoteNodeData,
  defaultNoteNodeData,
  type StartNodeData,
  normalizeStartNodeData,
  startScheduleIsPast,
  browserIanaTimezone,
  todayInputDateValue,
  currentInputTimeValue,
  visualChainIsEventTrigger,
  type LaunchMode,
} from './mailingTypes';
import {
  canvasNodeRingStatus,
  type CanvasNodeRingStatus,
  type ChannelNodeReadinessOpts,
} from './visualFlowReadiness';
import { useSmsPageDesign, SMS_PAGE, smsPageTokens } from '../../theme/smsPageDesign';
import { applyPersonalization, SAMPLE_AUDIENCE_USER, smsGsmUnits, smsLengthWarning } from '../../domain/personalization';
import { useMailingFlowLinkedCampaignsLabel } from './mailingFlowLinkedCampaignsContext';
import { useMailingFlowCampaignSubscriberListIds } from './mailingFlowCampaignSubscriberContext';
import { useMailingViberSmsCascadeContext } from './mailingViberSmsCascadeContext';
import {
  useSmsProviderCatalog,
  useViberProviderCatalog,
  resolveAlphaSelectValue,
  mapViberApiMessageKindToChannelType,
} from '../../api/smsProviderCatalog';
import { useEmailCabinetCatalog, useEmailMessageTemplates, fetchEmailTemplateByRef } from '../../api/emailCabinetApi';

/** Пустой Outlined Select + плавающая подпись без shrink даёт наложение «Не выбрано» / текста label. */
const CATALOG_SELECT_FORM_CONTROL_PROPS = { InputLabelProps: { shrink: true } } as const;

type CatalogSelectRow = { id: number; name: string; count?: number };

/** Закрытый Select с displayEmpty иначе рисует label и текст value в одной зоне — одна строка через renderValue. */
function catalogSelectRenderValue(selected: unknown, rows: CatalogSelectRow[], notChosen: string): string {
  const s = String(selected ?? '').trim();
  if (s === '') return notChosen;
  const idNum = Number(s);
  const row = rows.find((r) => String(r.id) === s || (Number.isFinite(idNum) && r.id === idNum));
  if (!row) return notChosen;
  if (row.count != null && typeof row.count === 'number') return `${row.name} (${row.count})`;
  return row.name;
}

/** API иногда отдаёт ключи вроде app.viber.messageTypes.TEXT вместо человекочитаемого name. */
function viberProviderMessageKindLabel(o: { id: string; name: string }, t: TFunction): string {
  const raw = (o.name || '').trim();
  if (!raw || raw.includes('messageTypes') || raw.startsWith('app.')) {
    const ch = mapViberApiMessageKindToChannelType(o.id);
    return t(`flowNodes.viberMsgTypes.${ch}`);
  }
  return raw;
}

function formatStartScheduleDate(value: string): string {
  const [year, month, day] = value.split('-');
  if (!year || !month || !day) return value;
  return `${day}.${month}.${year}`;
}

const handleStyle = { width: 10, height: 10, borderWidth: 2 } as const;

function flowNodeRingSx(theme: Theme, status: CanvasNodeRingStatus): Record<string, unknown> {
  if (status === 'neutral') return {};
  const main = status === 'success' ? theme.palette.success.main : theme.palette.warning.main;
  return {
    outline: `2px solid ${main}`,
    outlineOffset: 2,
    transition: theme.transitions.create(['box-shadow', 'outline-color'], { duration: 220 }),
    boxShadow: `0 0 14px ${alpha(main, status === 'success' ? 0.42 : 0.38)}`,
  };
}

const nodeShellSx = (isSmsLight: boolean) => ({
  minWidth: 200,
  maxWidth: 260,
  borderRadius: '8px',
  boxShadow: smsPageTokens(isSmsLight).shadow,
  border: `1px solid ${smsPageTokens(isSmsLight).outlineBorder}`,
  bgcolor: smsPageTokens(isSmsLight).white,
  overflow: 'hidden',
});

/** Лимит символов текста SMS в форме (договорённость UI). */
const SMS_BODY_MAX_CHARS = 1530;

/** Лимит текста сообщения Viber в узле (как в типичном интерфейсе рассылки). */
const VIBER_BODY_MAX_CHARS = 1000;

const VIBER_EMOJI_GRID = [
  '😀',
  '😃',
  '😄',
  '😁',
  '😅',
  '😂',
  '🙂',
  '😉',
  '😍',
  '😘',
  '😊',
  '😎',
  '👍',
  '👎',
  '❤️',
  '🔥',
  '✨',
  '🎉',
  '📱',
  '🔗',
  '⭐',
  '✅',
] as const;

const logicBranchShellSx = (isSmsLight: boolean, accentBg: string) => ({
  minWidth: 236,
  maxWidth: 280,
  borderRadius: '8px',
  boxShadow: smsPageTokens(isSmsLight).shadow,
  border: `1px solid ${smsPageTokens(isSmsLight).outlineBorder}`,
  bgcolor: smsPageTokens(isSmsLight).white,
  overflow: 'visible',
  position: 'relative' as const,
  borderTop: `3px solid ${accentBg}`,
});

function useConditionModeOptions(): { value: ConditionMode; label: string }[] {
  const { t } = useTranslation();
  return useMemo(
    () =>
      (['delivery', 'click', 'segment', 'custom'] as const).map((value) => ({
        value,
        label: t(`flowNodes.conditionModes.${value}`),
      })),
    [t]
  );
}

export const MailingStartNode = memo(function MailingStartNode(props: NodeProps) {
  const { t } = useTranslation();
  const { data } = props;
  const d = normalizeStartNodeData(data);
  const { isSmsLight } = useSmsPageDesign();
  const linkedCampaignLine = useMailingFlowLinkedCampaignsLabel();
  const isEvent = d.launchMode === 'event';
  const hasSchedule = !isEvent && Boolean(d.scheduleDate && d.scheduleTime);
  const hasEventTime = isEvent && Boolean(d.scheduleTime);
  const scheduleText = isEvent
    ? hasEventTime
      ? t('flowNodes.startEventValue', {
          type: t(`flowNodes.eventTypes.${d.eventType ?? 'birthday'}`),
          time: d.scheduleTime,
        })
      : t('flowNodes.startEventMissing')
    : hasSchedule
      ? t('flowNodes.startScheduleValue', {
          date: formatStartScheduleDate(d.scheduleDate ?? ''),
          time: d.scheduleTime,
        })
      : t('flowNodes.startScheduleMissing');

  return (
    <Box sx={nodeShellSx(isSmsLight)}>
      <Box
        sx={{
          px: 1.5,
          py: 1.25,
          bgcolor: smsPageTokens(isSmsLight).card,
        }}
      >
        <Stack spacing={0.75}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StartIcon sx={{ fontSize: 20, color: 'primary.main', flexShrink: 0 }} />
            <Typography variant="subtitle2" fontWeight={600}>
              {t('flowNodes.startTitle')}
            </Typography>
          </Box>
          <Tooltip title={scheduleText} placement="top">
            <Typography
              variant="caption"
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                width: 'fit-content',
                maxWidth: 228,
                px: 0.75,
                py: 0.35,
                borderRadius: '999px',
                lineHeight: 1.25,
                color: hasSchedule || hasEventTime ? 'text.secondary' : theme.palette.warning.dark,
                bgcolor:
                  hasSchedule || hasEventTime
                    ? smsPageTokens(isSmsLight).row
                    : alpha(theme.palette.warning.main, 0.14),
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              })}
            >
              <DelayIcon sx={{ fontSize: 14, flexShrink: 0 }} />
              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {scheduleText}
              </Box>
            </Typography>
          </Tooltip>
          {linkedCampaignLine ? (
            <Tooltip title={linkedCampaignLine} placement="top">
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  lineHeight: 1.25,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 228,
                }}
              >
                {linkedCampaignLine}
              </Typography>
            </Tooltip>
          ) : null}
        </Stack>
      </Box>
      <Handle type="source" position={Position.Right} id="out" style={handleStyle} />
    </Box>
  );
});

export const StartNodeInspectorForm = memo(function StartNodeInspectorForm({
  id,
  data,
}: {
  id: string;
  data: unknown;
}) {
  const { t } = useTranslation();
  const d = normalizeStartNodeData(data);
  const { setNodes } = useReactFlow();
  const now = useMemo(() => new Date(), []);
  const today = todayInputDateValue(now);
  const minTime = d.scheduleDate === today ? currentInputTimeValue(now) : undefined;
  const scheduleInPast = !d.launchMode || d.launchMode === 'schedule' ? startScheduleIsPast(d.scheduleDate, d.scheduleTime) : false;
  const isEvent = d.launchMode === 'event';

  const patch = useCallback(
    (updates: Partial<StartNodeData>) => {
      setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id !== id) return node;
          const cur = normalizeStartNodeData(node.data);
          const next = { ...cur, ...updates };
          if (updates.launchMode === 'event') {
            next.trigger = 'event';
            if (!String(next.eventTimezone ?? '').trim()) {
              next.eventTimezone = browserIanaTimezone();
            }
          } else if (updates.launchMode === 'schedule') {
            next.trigger = 'schedule';
          }
          return { ...node, data: next };
        })
      );
    },
    [id, setNodes]
  );

  useEffect(() => {
    if (isEvent && !String(d.eventTimezone ?? '').trim()) {
      patch({ eventTimezone: browserIanaTimezone() });
    }
  }, [isEvent, d.eventTimezone, patch]);

  return (
    <Stack spacing={1.1} className="nodrag nopan" onWheelCapture={(e) => e.stopPropagation()}>
      <FormControl fullWidth size="small" className="nodrag nopan">
        <InputLabel id={`${id}-launch-mode`}>{t('flowNodes.launchModeLabel')}</InputLabel>
        <Select
          labelId={`${id}-launch-mode`}
          label={t('flowNodes.launchModeLabel')}
          value={d.launchMode ?? 'schedule'}
          onChange={(e) => patch({ launchMode: e.target.value as LaunchMode })}
        >
          <MenuItem value="schedule">{t('flowNodes.launchModeSchedule')}</MenuItem>
          <MenuItem value="event">{t('flowNodes.launchModeEvent')}</MenuItem>
        </Select>
      </FormControl>

      {isEvent ? (
        <>
          <FormControl fullWidth size="small" className="nodrag nopan">
            <InputLabel id={`${id}-event-type`}>{t('flowNodes.eventTypeLabel')}</InputLabel>
            <Select
              labelId={`${id}-event-type`}
              label={t('flowNodes.eventTypeLabel')}
              value={d.eventType ?? 'birthday'}
              onChange={(e) => patch({ eventType: e.target.value as StartNodeData['eventType'] })}
            >
              <MenuItem value="birthday">{t('flowNodes.eventTypes.birthday')}</MenuItem>
              <MenuItem value="registration">{t('flowNodes.eventTypes.registration')}</MenuItem>
              <MenuItem value="custom">{t('flowNodes.eventTypes.custom')}</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            fullWidth
            label={t('flowNodes.eventFieldKeyLabel')}
            placeholder={t('flowNodes.eventFieldKeyPlaceholder')}
            value={d.eventFieldKey ?? ''}
            onChange={(e) => patch({ eventFieldKey: e.target.value })}
            className="nodrag nopan"
          />
          <TextField
            size="small"
            label={t('flowNodes.eventSendTimeLabel')}
            type="time"
            InputLabelProps={{ shrink: true }}
            value={d.scheduleTime ?? ''}
            onChange={(e) => patch({ scheduleTime: e.target.value })}
            className="nodrag nopan"
            fullWidth
          />
          <Typography variant="caption" color="text.secondary" display="block">
            {t('flowNodes.eventTimezoneHint', {
              tz: d.eventTimezone?.trim() || browserIanaTimezone(),
            })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('flowNodes.eventModeHint')}
          </Typography>
        </>
      ) : (
        <>
          <Typography variant="subtitle2" fontWeight={600}>
            {t('flowNodes.smsScheduleSection')}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
            <TextField
              size="small"
              label={t('flowNodes.smsScheduleDate')}
              type="date"
              InputLabelProps={{ shrink: true }}
              value={d.scheduleDate ?? ''}
              onChange={(e) => patch({ scheduleDate: e.target.value })}
              error={scheduleInPast}
              className="nodrag nopan"
              inputProps={{ min: today }}
              sx={{ flex: 1, minWidth: 0 }}
            />
            <TextField
              size="small"
              label={t('flowNodes.smsScheduleTime')}
              type="time"
              InputLabelProps={{ shrink: true }}
              value={d.scheduleTime ?? ''}
              onChange={(e) => patch({ scheduleTime: e.target.value })}
              error={scheduleInPast}
              className="nodrag nopan"
              inputProps={{ ...(minTime ? { min: minTime } : {}) }}
              sx={{ flex: 1, minWidth: 0 }}
            />
          </Stack>
          {scheduleInPast ? (
            <Typography variant="caption" color="error" sx={{ display: 'block' }}>
              {t('flowNodes.startSchedulePastError')}
            </Typography>
          ) : null}
        </>
      )}
    </Stack>
  );
});

export const MailingEndNode = memo(function MailingEndNode(_props: NodeProps) {
  const { t } = useTranslation();
  const { isSmsLight } = useSmsPageDesign();
  return (
    <Box sx={nodeShellSx(isSmsLight)}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1.25,
          bgcolor: smsPageTokens(isSmsLight).row,
        }}
      >
        <EndIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
        <Typography variant="subtitle2" fontWeight={600}>
          {t('flowNodes.endTitle')}
        </Typography>
      </Box>
      <Handle type="target" position={Position.Left} id="in" style={handleStyle} />
    </Box>
  );
});

export const ConditionNodeInspectorForm = memo(function ConditionNodeInspectorForm({ id, data }: { id: string; data: unknown }) {
  const { t } = useTranslation();
  const conditionModeOptions = useConditionModeOptions();
  const d = normalizeConditionNodeData(data);
  const { setNodes } = useReactFlow();

  const patch = useCallback(
    (updates: Partial<ConditionNodeData>) => {
      setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id !== id) return node;
          const cur = normalizeConditionNodeData(node.data);
          return { ...node, data: { ...cur, ...updates } };
        })
      );
    },
    [id, setNodes]
  );

  return (
    <Stack spacing={1.25} className="nodrag nopan">
      <TextField
        size="small"
        fullWidth
        label={t('flowNodes.title')}
        value={d.title}
        onChange={(e) => patch({ title: e.target.value })}
        className="nodrag nopan"
      />
      <FormControl size="small" fullWidth className="nodrag nopan">
        <InputLabel id={`cond-mode-${id}`}>{t('flowNodes.type')}</InputLabel>
        <Select
          labelId={`cond-mode-${id}`}
          label={t('flowNodes.type')}
          value={d.mode}
          onChange={(e) => patch({ mode: e.target.value as ConditionMode })}
          className="nodrag nopan"
          MenuProps={{ disablePortal: false, PaperProps: { sx: { zIndex: 20000 } } }}
        >
          {conditionModeOptions.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        size="small"
        fullWidth
        label={t('flowNodes.conditionDescription')}
        placeholder={t('flowNodes.conditionPlaceholder')}
        multiline
        minRows={3}
        value={d.description}
        onChange={(e) => patch({ description: e.target.value })}
        className="nodrag nopan"
      />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
        {t('flowNodes.conditionOutputsHint')}
      </Typography>
    </Stack>
  );
});

export const MailingConditionNode = memo(function MailingConditionNode(props: NodeProps) {
  const { data, id, type } = props;
  const { t } = useTranslation();
  const d = normalizeConditionNodeData(data);
  const { isSmsLight } = useSmsPageDesign();
  const modeLabel = t(`flowNodes.conditionModes.${d.mode}`);
  const titleLine = d.title.trim() || t('flowEditor.nodeTapToConfigure');
  const { getEdges } = useReactFlow();
  const ring = canvasNodeRingStatus({ id, type, data }, getEdges());

  return (
    <Box
      sx={(theme) => ({
        ...logicBranchShellSx(isSmsLight, '#00897B'),
        ...flowNodeRingSx(theme, ring),
      })}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          bgcolor: smsPageTokens(isSmsLight).card,
          borderBottom: `1px solid ${smsPageTokens(isSmsLight).divider}`,
        }}
      >
        <ConditionIcon sx={{ fontSize: 20, color: 'secondary.main' }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {t('flowNodes.conditionTitle')}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.25 }} noWrap>
            {titleLine}
          </Typography>
          <Typography variant="caption" color="primary.main" sx={{ display: 'block', mt: 0.25 }}>
            {modeLabel}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ position: 'relative', minHeight: 44, px: 1.5, pb: 1, pt: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {t('flowNodes.conditionOutputsHint')}
        </Typography>
        <Typography
          variant="caption"
          fontWeight={700}
          color="success.main"
          sx={{ position: 'absolute', right: 28, bottom: 30, pointerEvents: 'none' }}
        >
          {t('flowNodes.yes')}
        </Typography>
        <Typography
          variant="caption"
          fontWeight={700}
          color="text.secondary"
          sx={{ position: 'absolute', right: 28, bottom: 8, pointerEvents: 'none' }}
        >
          {t('flowNodes.no')}
        </Typography>
        <Handle type="source" position={Position.Right} id="yes" style={{ ...handleStyle, top: 'auto', bottom: 26 }} />
        <Handle type="source" position={Position.Right} id="no" style={{ ...handleStyle, top: 'auto', bottom: 4 }} />
      </Box>
      <Handle type="target" position={Position.Left} id="in" style={{ ...handleStyle, top: '50%' }} />
    </Box>
  );
});

export const AbTestNodeInspectorForm = memo(function AbTestNodeInspectorForm({ id, data }: { id: string; data: unknown }) {
  const { t } = useTranslation();
  const d = normalizeAbTestNodeData(data);
  const { setNodes } = useReactFlow();
  const [splitText, setSplitText] = useState(() => String(d.splitPercentA));

  useEffect(() => {
    setSplitText(String(d.splitPercentA));
  }, [d.splitPercentA, id]);

  const patch = useCallback(
    (updates: Partial<AbTestNodeData>) => {
      setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id !== id) return node;
          const cur = normalizeAbTestNodeData(node.data);
          return { ...node, data: { ...cur, ...updates } };
        })
      );
    },
    [id, setNodes]
  );

  return (
    <Stack spacing={1.25} className="nodrag nopan">
      <TextField
        size="small"
        fullWidth
        label={t('flowNodes.branchALabel')}
        value={d.labelA}
        onChange={(e) => patch({ labelA: e.target.value })}
        className="nodrag nopan"
      />
      <TextField
        size="small"
        fullWidth
        label={t('flowNodes.branchBLabel')}
        value={d.labelB}
        onChange={(e) => patch({ labelB: e.target.value })}
        className="nodrag nopan"
      />
      <TextField
        size="small"
        fullWidth
        label={t('flowNodes.splitPercent')}
        type="text"
        inputMode="numeric"
        value={splitText}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '') {
            setSplitText('');
            return;
          }
          if (!/^\d{1,3}$/.test(raw)) return;
          const n = Math.min(100, Math.max(0, parseInt(raw, 10)));
          setSplitText(raw);
          patch({ splitPercentA: n });
        }}
        onBlur={() => {
          if (splitText === '') {
            setSplitText('50');
            patch({ splitPercentA: 50 });
            return;
          }
          const n = Math.min(100, Math.max(0, parseInt(splitText, 10) || 0));
          setSplitText(String(n));
          patch({ splitPercentA: n });
        }}
        helperText={t('flowNodes.splitHelper', { pct: 100 - d.splitPercentA })}
        className="nodrag nopan"
      />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
        {t('flowNodes.abOutputsHint')}
      </Typography>
    </Stack>
  );
});

export const MailingAbTestNode = memo(function MailingAbTestNode(props: NodeProps) {
  const { data, id, type } = props;
  const { t } = useTranslation();
  const d = normalizeAbTestNodeData(data);
  const { isSmsLight } = useSmsPageDesign();
  const { getEdges } = useReactFlow();
  const ring = canvasNodeRingStatus({ id, type, data }, getEdges());

  return (
    <Box
      sx={(theme) => ({
        ...logicBranchShellSx(isSmsLight, '#5E35B1'),
        ...flowNodeRingSx(theme, ring),
      })}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          bgcolor: smsPageTokens(isSmsLight).card,
          borderBottom: `1px solid ${smsPageTokens(isSmsLight).divider}`,
        }}
      >
        <AbTestIcon sx={{ fontSize: 20, color: 'secondary.main' }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {t('flowNodes.abTitle')}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {d.splitPercentA}% / {100 - d.splitPercentA}% · {d.labelA || 'A'} / {d.labelB || 'B'}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ position: 'relative', minHeight: 44, px: 1.5, pb: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {t('flowNodes.abOutputsHint')}
        </Typography>
        <Typography
          variant="caption"
          fontWeight={700}
          color="primary.main"
          sx={{ position: 'absolute', right: 28, bottom: 30, pointerEvents: 'none' }}
        >
          A
        </Typography>
        <Typography
          variant="caption"
          fontWeight={700}
          color="secondary.main"
          sx={{ position: 'absolute', right: 28, bottom: 8, pointerEvents: 'none' }}
        >
          B
        </Typography>
        <Handle type="source" position={Position.Right} id="a" style={{ ...handleStyle, top: 'auto', bottom: 26 }} />
        <Handle type="source" position={Position.Right} id="b" style={{ ...handleStyle, top: 'auto', bottom: 4 }} />
      </Box>
      <Handle type="target" position={Position.Left} id="in" style={{ ...handleStyle, top: '50%' }} />
    </Box>
  );
});

export const ChannelNodeInspectorForm = memo(function ChannelNodeInspectorForm({ id, data }: { id: string; data: unknown }) {
  const { t } = useTranslation();
  const d = normalizeChannelNodeData(data);
  const { setNodes, getNodes } = useReactFlow();
  const isEventChain = visualChainIsEventTrigger(getNodes());
  const [viberEmojiAnchor, setViberEmojiAnchor] = useState<HTMLElement | null>(null);

  const patch = useCallback(
    (updates: Record<string, unknown>) => {
      setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id !== id) return node;
          const cur = normalizeChannelNodeData(node.data);
          return { ...node, data: { ...cur, ...updates } as ChannelNodeData };
        })
      );
    },
    [id, setNodes]
  );

  const smsCatalog = useSmsProviderCatalog(d.channel === 'sms');
  const viberCatalog = useViberProviderCatalog(d.channel === 'viber');
  const emailCabinet = useEmailCabinetCatalog(d.channel === 'email');
  const emailMsgTemplates = useEmailMessageTemplates(d.channel === 'email');
  const [emailTplApplying, setEmailTplApplying] = useState(false);
  const [emailTplApplyErr, setEmailTplApplyErr] = useState<string | null>(null);
  const [emailBodyView, setEmailBodyView] = useState<'edit' | 'preview'>('preview');
  useEffect(() => {
    setEmailTplApplyErr(null);
    setEmailTplApplying(false);
    setEmailBodyView('preview');
  }, [id]);

  const viberSmsCascadeCtx = useMailingViberSmsCascadeContext();
  const showViberSmsForwardingSwitch = Boolean(
    viberSmsCascadeCtx &&
      (d.channel === 'sms' || d.channel === 'viber') &&
      viberSmsCascadeCtx.firstChannelNodeId === id
  );

  const plainViberMessageTypes = useMemo(
    () => (viberCatalog.viberMessageTypes ?? []).filter((o) => mapViberApiMessageKindToChannelType(o.id) === 'plain'),
    [viberCatalog.viberMessageTypes]
  );
  const plainViberMessageKindId = plainViberMessageTypes[0]?.id ?? '';
  const viberMessageType = d.channel === 'viber' ? d.messageType : undefined;

  useEffect(() => {
    if (d.channel !== 'viber') return;
    if (viberMessageType === 'plain') return;
    patch({ messageType: 'plain', viberMessageKindId: plainViberMessageKindId });
  }, [d.channel, viberMessageType, patch, plainViberMessageKindId]);

  const emailSenderRows: CatalogSelectRow[] = useMemo(
    () =>
      d.channel === 'email'
        ? emailCabinet.catalog.senders.map((s) => ({ id: s.id, name: s.email }))
        : [],
    [d.channel, emailCabinet.catalog.senders]
  );

  const fieldStack = (
    <Stack
      spacing={1.1}
      className="nodrag nopan"
      onWheelCapture={(e) => e.stopPropagation()}
      sx={{
        p: 1.5,
        maxHeight: 'calc(100vh - 160px)',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
      }}
    >
      {isEventChain && (d.channel === 'sms' || d.channel === 'viber' || d.channel === 'email') ? (
        <TextField
          size="small"
          fullWidth
          type="number"
          inputProps={{ min: 0, step: 1 }}
          label={t('flowNodes.triggerOffsetDaysLabel')}
          helperText={t('flowNodes.triggerOffsetDaysHint')}
          value={d.triggerOffsetDays}
          onChange={(e) => patch({ triggerOffsetDays: e.target.value })}
          className="nodrag nopan"
        />
      ) : null}
      {d.channel === 'push' && (
        <>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mt: -0.25 }}>
            {t('flowNodes.commonSendSection')}
          </Typography>
          <TextField
            size="small"
            fullWidth
            label={t('flowNodes.commonSenderLabel')}
            placeholder={t('flowNodes.pushSenderPlaceholder')}
            value={d.senderName}
            onChange={(e) => patch({ senderName: e.target.value })}
            className="nodrag nopan"
          />
          <TextField
            size="small"
            fullWidth
            label={t('flowNodes.pushTitle')}
            value={d.title}
            onChange={(e) => patch({ title: e.target.value })}
            className="nodrag nopan"
          />
          <TextField
            size="small"
            fullWidth
            label={t('flowNodes.pushBody')}
            multiline
            minRows={2}
            value={d.body}
            onChange={(e) => patch({ body: e.target.value })}
            className="nodrag nopan"
          />
          <TextField
            size="small"
            fullWidth
            label={t('flowNodes.pushImageUrl')}
            value={d.imageUrl}
            onChange={(e) => patch({ imageUrl: e.target.value })}
            className="nodrag nopan"
          />
          <TextField
            size="small"
            fullWidth
            label={t('flowNodes.pushActionUrl')}
            value={d.actionUrl}
            onChange={(e) => patch({ actionUrl: e.target.value })}
            className="nodrag nopan"
          />
        </>
      )}
      {d.channel === 'sms' && (
        <>
          {smsCatalog.alphanames.length > 0 ? (
            <FormControl fullWidth size="small" className="nodrag nopan" {...CATALOG_SELECT_FORM_CONTROL_PROPS}>
              <InputLabel id={`${id}-sms-alpha`} shrink>
                {t('flowNodes.smsAlphaname')}
              </InputLabel>
              <Select
                labelId={`${id}-sms-alpha`}
                label={t('flowNodes.smsAlphaname')}
                value={resolveAlphaSelectValue(d.senderName, smsCatalog.alphanames)}
                onChange={(e) => patch({ senderName: String(e.target.value) })}
                displayEmpty
                renderValue={(v) =>
                  catalogSelectRenderValue(v, smsCatalog.alphanames, t('flowNodes.smsCatalogNotChosen'))
                }
              >
                <MenuItem value="">{t('flowNodes.smsCatalogNotChosen')}</MenuItem>
                {smsCatalog.alphanames.map((row) => (
                  <MenuItem key={row.id} value={String(row.id)}>
                    {row.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField
              size="small"
              fullWidth
              disabled
              value=""
              label={t('flowNodes.smsAlphaname')}
              className="nodrag nopan"
            />
          )}
          {(smsCatalog.smsTemplates?.length ?? 0) > 0 ? (
            <FormControl fullWidth size="small" className="nodrag nopan" {...CATALOG_SELECT_FORM_CONTROL_PROPS}>
              <InputLabel id={`${id}-sms-tpl`} shrink>
                {t('flowNodes.smsTemplateCatalog')}
              </InputLabel>
              <Select
                labelId={`${id}-sms-tpl`}
                label={t('flowNodes.smsTemplateCatalog')}
                value={resolveAlphaSelectValue(d.messageTemplateQuery, smsCatalog.smsTemplates ?? [])}
                onChange={(e) => {
                  const val = String(e.target.value);
                  const rows = smsCatalog.smsTemplates ?? [];
                  const picked = rows.find((r) => String(r.id) === val);
                  const msg = picked?.message != null ? String(picked.message).trim() : '';
                  patch({
                    messageTemplateQuery: val,
                    ...(msg !== '' ? { body: msg } : {}),
                  });
                }}
                displayEmpty
                renderValue={(v) =>
                  catalogSelectRenderValue(
                    v,
                    smsCatalog.smsTemplates ?? [],
                    t('flowNodes.smsCatalogNotChosen'),
                  )
                }
              >
                <MenuItem value="">{t('flowNodes.smsCatalogNotChosen')}</MenuItem>
                {(smsCatalog.smsTemplates ?? []).map((row) => (
                  <MenuItem key={row.id} value={String(row.id)}>
                    {row.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}
          <Typography variant="subtitle2" fontWeight={600}>
            {t('flowNodes.smsMessageSection')}
          </Typography>
          <TextField
            size="small"
            fullWidth
            label={t('flowNodes.smsMessageTextLabel')}
            placeholder={t('flowNodes.smsMessagePlaceholder')}
            multiline
            minRows={3}
            value={d.body}
            onChange={(e) => patch({ body: e.target.value })}
            className="nodrag nopan"
            inputProps={{ maxLength: SMS_BODY_MAX_CHARS }}
          />
          {(() => {
            const sampleText = applyPersonalization(d.body, SAMPLE_AUDIENCE_USER);
            const { segments } = smsGsmUnits(sampleText);
            const len = [...sampleText].length;
            const long = len > SMS_BODY_MAX_CHARS;
            return (
              <Stack spacing={0.75}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('flowNodes.smsPartsCount', { count: segments })}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                    <Typography variant="caption" color={long ? 'warning.main' : 'text.secondary'}>
                      {t('flowNodes.smsCharCounter', { current: len, max: SMS_BODY_MAX_CHARS })}
                    </Typography>
                    <Tooltip
                      title={
                        <Box sx={{ maxWidth: 300 }}>
                          <Typography variant="caption" display="block">
                            {t('flowNodes.smsCharCounterTooltip')}
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                            {t('flowNodes.smsParameterizedHint')}
                          </Typography>
                        </Box>
                      }
                      componentsProps={{ tooltip: { sx: { bgcolor: 'grey.900', maxWidth: 320 } } }}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled', verticalAlign: 'middle' }} />
                    </Tooltip>
                  </Box>
                </Box>
                {(() => {
                  const w = smsLengthWarning(sampleText);
                  return w ? (
                    <Typography variant="caption" color="warning.main" display="block">
                      {w}
                    </Typography>
                  ) : null;
                })()}
              </Stack>
            );
          })()}
        </>
      )}
      {d.channel === 'email' && (
        <>
          {emailCabinet.errors.length > 0 ? (
            <Typography variant="caption" color="warning.main" display="block" sx={{ lineHeight: 1.45 }}>
              {t('flowNodes.emailCatalogPartialError', { details: emailCabinet.errors.join(' · ') })}
            </Typography>
          ) : null}

          {emailSenderRows.length > 0 ? (
            <FormControl
              fullWidth
              size="small"
              className="nodrag nopan"
              {...CATALOG_SELECT_FORM_CONTROL_PROPS}
            >
              <InputLabel id={`${id}-email-sender`} shrink>
                {t('flowNodes.emailSenderFromCatalog')}
              </InputLabel>
              <Select
                labelId={`${id}-email-sender`}
                label={t('flowNodes.emailSenderFromCatalog')}
                value={resolveAlphaSelectValue(d.senderName, emailSenderRows)}
                onChange={(e) => patch({ senderName: String(e.target.value) })}
                displayEmpty
                renderValue={(v) =>
                  catalogSelectRenderValue(v, emailSenderRows, t('flowNodes.smsCatalogNotChosen'))
                }
                MenuProps={{ disablePortal: false, PaperProps: { sx: { zIndex: 20000 } } }}
              >
                <MenuItem value="">{t('flowNodes.smsCatalogNotChosen')}</MenuItem>
                {emailSenderRows.map((row) => (
                  <MenuItem key={row.id} value={String(row.id)}>
                    {row.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField
              size="small"
              fullWidth
              label={t('flowNodes.commonSenderLabel')}
              placeholder={t('flowNodes.emailSenderPlaceholder')}
              helperText={
                emailCabinet.catalog.senders.length === 0 ? t('flowNodes.emailSenderCatalogEmpty') : undefined
              }
              value={d.senderName}
              onChange={(e) => patch({ senderName: e.target.value })}
              className="nodrag nopan"
            />
          )}
          <TextField
            size="small"
            fullWidth
            label={t('flowNodes.emailSenderDisplayName')}
            placeholder={t('flowNodes.emailSenderDisplayNamePlaceholder')}
            value={d.senderDisplayName}
            onChange={(e) => patch({ senderDisplayName: e.target.value })}
            className="nodrag nopan"
          />

          {emailMsgTemplates.listError ? (
            <Typography variant="caption" color="warning.main" display="block" sx={{ lineHeight: 1.45 }}>
              {t('flowNodes.emailTemplateListError', { message: emailMsgTemplates.listError })}
            </Typography>
          ) : null}
          {d.channel === 'email' &&
          (emailMsgTemplates.rows.length > 0 || Boolean(d.messageTemplateQuery.trim())) ? (
            <FormControl fullWidth size="small" className="nodrag nopan" {...CATALOG_SELECT_FORM_CONTROL_PROPS}>
              <InputLabel id={`${id}-email-tpl`} shrink>
                {t('flowNodes.emailTemplateCatalog')}
              </InputLabel>
              <Select
                labelId={`${id}-email-tpl`}
                label={t('flowNodes.emailTemplateCatalog')}
                value={d.messageTemplateQuery}
                disabled={emailTplApplying}
                onChange={(e) => {
                  const ref = String(e.target.value);
                  if (!ref) {
                    setEmailTplApplyErr(null);
                    patch({ messageTemplateQuery: '' });
                    return;
                  }
                  setEmailTplApplying(true);
                  setEmailTplApplyErr(null);
                  void fetchEmailTemplateByRef(ref)
                    .then((det) => {
                      patch({
                        messageTemplateQuery: ref,
                        ...(det.title.trim() ? { title: det.title } : {}),
                        ...(det.html.trim() ? { body: det.html } : {}),
                        ...(det.preheader.trim() ? { preheader: det.preheader } : {}),
                      });
                    })
                    .catch((e) => {
                      setEmailTplApplyErr(e instanceof Error ? e.message : String(e));
                      patch({ messageTemplateQuery: ref });
                    })
                    .finally(() => setEmailTplApplying(false));
                }}
                displayEmpty
                renderValue={(v) => {
                  const s = String(v || '');
                  if (!s) return t('flowNodes.smsCatalogNotChosen');
                  const row = emailMsgTemplates.rows.find((r) => r.ref === s);
                  return row ? row.title : s;
                }}
                MenuProps={{ disablePortal: false, PaperProps: { sx: { zIndex: 20000 } } }}
              >
                <MenuItem value="">{t('flowNodes.smsCatalogNotChosen')}</MenuItem>
                {d.messageTemplateQuery.trim() &&
                !emailMsgTemplates.rows.some((r) => r.ref === d.messageTemplateQuery) ? (
                  <MenuItem value={d.messageTemplateQuery}>{d.messageTemplateQuery}</MenuItem>
                ) : null}
                {emailMsgTemplates.rows.map((row) => (
                  <MenuItem key={row.ref} value={row.ref}>
                    {row.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}
          {d.channel === 'email' && emailTplApplying ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="caption" color="text.secondary">
                {t('flowNodes.emailTemplateApplying')}
              </Typography>
            </Box>
          ) : null}
          {emailTplApplyErr ? (
            <Typography variant="caption" color="error" display="block" sx={{ lineHeight: 1.45 }}>
              {t('flowNodes.emailTemplateFetchError', { message: emailTplApplyErr })}
            </Typography>
          ) : null}

          <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 1 }}>
            {t('flowNodes.emailMessageSection')}
          </Typography>
          <TextField
            size="small"
            fullWidth
            label={t('flowNodes.emailSubject')}
            value={d.title}
            onChange={(e) => patch({ title: e.target.value })}
            className="nodrag nopan"
          />
          <TextField
            size="small"
            fullWidth
            label={t('flowNodes.emailPreheader')}
            value={d.preheader}
            onChange={(e) => patch({ preheader: e.target.value })}
            className="nodrag nopan"
          />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {t('flowNodes.emailBody')}
            </Typography>
            <ToggleButtonGroup
              size="small"
              value={emailBodyView}
              exclusive
              onChange={(_, v) => v && setEmailBodyView(v)}
              aria-label={t('flowNodes.emailBodyViewModeAria')}
            >
              <ToggleButton value="preview" sx={{ px: 1.25, py: 0.25, textTransform: 'none' }}>
                {t('flowNodes.emailBodyViewPreview')}
              </ToggleButton>
              <ToggleButton value="edit" sx={{ px: 1.25, py: 0.25, textTransform: 'none' }}>
                {t('flowNodes.emailBodyViewEdit')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {emailBodyView === 'edit' ? (
            <TextField
              size="small"
              fullWidth
              label={t('flowNodes.emailBodyHtmlHint')}
              multiline
              minRows={6}
              value={d.body}
              onChange={(e) => patch({ body: e.target.value })}
              className="nodrag nopan"
            />
          ) : (
            <Box
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                bgcolor: 'background.paper',
                minHeight: 200,
                maxHeight: 420,
              }}
            >
              <iframe
                title={t('flowNodes.emailBodyPreviewFrameTitle')}
                sandbox=""
                srcDoc={
                  d.body.trim()
                    ? d.body
                    : `<!DOCTYPE html><html><head><meta charset="utf-8"></head><body style="margin:0;padding:16px;font-family:system-ui,sans-serif;font-size:14px;color:#888;">${t('flowNodes.emailPreviewEmpty')}</body></html>`
                }
                key={`${id}-${d.body.length}`}
                style={{
                  display: 'block',
                  width: '100%',
                  height: 360,
                  border: 'none',
                  verticalAlign: 'top',
                }}
              />
            </Box>
          )}
        </>
      )}
      {d.channel === 'viber' && (
        <>
          {viberCatalog.alphanames.length > 0 ? (
            <FormControl
              fullWidth
              size="small"
              className="nodrag nopan"
              sx={{ mt: 0.5 }}
              {...CATALOG_SELECT_FORM_CONTROL_PROPS}
            >
              <InputLabel id={`${id}-viber-sender`} shrink>
                {t('flowNodes.smsAlphaname')}
              </InputLabel>
              <Select
                labelId={`${id}-viber-sender`}
                label={t('flowNodes.smsAlphaname')}
                value={resolveAlphaSelectValue(d.viberId, viberCatalog.alphanames)}
                onChange={(e) => patch({ viberId: String(e.target.value) })}
                displayEmpty
                renderValue={(v) =>
                  catalogSelectRenderValue(v, viberCatalog.alphanames, t('flowNodes.smsCatalogNotChosen'))
                }
                MenuProps={{ disablePortal: false, PaperProps: { sx: { zIndex: 20000 } } }}
              >
                <MenuItem value="">{t('flowNodes.smsCatalogNotChosen')}</MenuItem>
                {viberCatalog.alphanames.map((row) => (
                  <MenuItem key={row.id} value={String(row.id)}>
                    {row.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField
              size="small"
              fullWidth
              disabled
              value=""
              label={t('flowNodes.smsAlphaname')}
              className="nodrag nopan"
              sx={{ mt: 0.5 }}
            />
          )}
          {viberCatalog.alphanames.length === 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {t('flowNodes.viberIdRegisterHint')}
            </Typography>
          ) : null}
          {(() => {
            const apiMsgTypes = plainViberMessageTypes;
            const viberKindSelectValue =
              apiMsgTypes.length === 0
                ? ''
                : apiMsgTypes.some((o) => o.id === d.viberMessageKindId)
                  ? d.viberMessageKindId
                  : apiMsgTypes[0].id;
            return apiMsgTypes.length > 0 ? (
              <FormControl size="small" fullWidth className="nodrag nopan" {...CATALOG_SELECT_FORM_CONTROL_PROPS}>
                <InputLabel id={`viber-mt-${id}`} shrink>
                  {t('flowNodes.viberMessageTypeLabel')}
                </InputLabel>
                <Select
                  labelId={`viber-mt-${id}`}
                  label={t('flowNodes.viberMessageTypeLabel')}
                  value={viberKindSelectValue}
                  onChange={(e) => {
                    const kid = String(e.target.value);
                    patch({
                      viberMessageKindId: kid,
                      messageType: 'plain',
                    });
                  }}
                  renderValue={(selected) => {
                    const o = apiMsgTypes.find((x) => x.id === String(selected));
                    return o ? viberProviderMessageKindLabel(o, t) : '';
                  }}
                  MenuProps={{ disablePortal: false, PaperProps: { sx: { zIndex: 20000 } } }}
                >
                  {apiMsgTypes.map((o) => (
                    <MenuItem key={o.id} value={o.id}>
                      {viberProviderMessageKindLabel(o, t)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <FormControl size="small" fullWidth className="nodrag nopan" {...CATALOG_SELECT_FORM_CONTROL_PROPS}>
                <InputLabel id={`viber-mt-${id}`} shrink>
                  {t('flowNodes.viberMessageTypeLabel')}
                </InputLabel>
                <Select
                  labelId={`viber-mt-${id}`}
                  label={t('flowNodes.viberMessageTypeLabel')}
                  value="plain"
                  onChange={(e) =>
                    patch({
                      messageType: 'plain',
                      viberMessageKindId: '',
                    })
                  }
                  MenuProps={{ disablePortal: false, PaperProps: { sx: { zIndex: 20000 } } }}
                >
                  <MenuItem value="plain">{t('flowNodes.viberMsgTypes.plain')}</MenuItem>
                </Select>
              </FormControl>
            );
          })()}
          {(viberCatalog.viberTemplates?.length ?? 0) > 0 ? (
            <FormControl fullWidth size="small" className="nodrag nopan" {...CATALOG_SELECT_FORM_CONTROL_PROPS}>
              <InputLabel id={`${id}-viber-tpl`} shrink>
                {t('flowNodes.viberTemplateCatalog')}
              </InputLabel>
              <Select
                labelId={`${id}-viber-tpl`}
                label={t('flowNodes.viberTemplateCatalog')}
                value={resolveAlphaSelectValue(d.messageTemplateQuery, viberCatalog.viberTemplates ?? [])}
                onChange={(e) => {
                  const val = String(e.target.value);
                  const rows = viberCatalog.viberTemplates ?? [];
                  const picked = rows.find((r) => String(r.id) === val);
                  const msg = picked?.message != null ? String(picked.message).trim() : '';
                  patch({
                    messageTemplateQuery: val,
                    ...(msg !== '' ? { body: msg } : {}),
                  });
                }}
                displayEmpty
                renderValue={(v) =>
                  catalogSelectRenderValue(
                    v,
                    viberCatalog.viberTemplates ?? [],
                    t('flowNodes.smsCatalogNotChosen'),
                  )
                }
                MenuProps={{ disablePortal: false, PaperProps: { sx: { zIndex: 20000 } } }}
              >
                <MenuItem value="">{t('flowNodes.smsCatalogNotChosen')}</MenuItem>
                {(viberCatalog.viberTemplates ?? []).map((row) => (
                  <MenuItem key={row.id} value={String(row.id)}>
                    {row.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField
              size="small"
              fullWidth
              label={t('flowNodes.viberTemplateLabel')}
              placeholder={t('flowNodes.viberTemplatePlaceholder')}
              value={d.messageTemplateQuery}
              onChange={(e) => patch({ messageTemplateQuery: e.target.value })}
              className="nodrag nopan"
            />
          )}
          <Typography variant="subtitle2" fontWeight={600}>
            {t('flowNodes.viberMessageSection')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('flowNodes.viberInsertVariables')}
          </Typography>
          <TextField
            size="small"
            fullWidth
            label={t('flowNodes.viberMessageTextLabel')}
            placeholder={t('flowNodes.viberMessagePlaceholder')}
            multiline
            minRows={4}
            value={d.body}
            onChange={(e) => patch({ body: e.target.value })}
            className="nodrag nopan"
            inputProps={{ maxLength: VIBER_BODY_MAX_CHARS }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant="text"
              className="nodrag nopan"
              color="primary"
              onClick={(e) => setViberEmojiAnchor(e.currentTarget)}
            >
              {t('flowNodes.viberPickEmoji')}
            </Button>
            <Typography variant="caption" color="text.secondary">
              {t('flowNodes.viberCharCounter', {
                current: [...d.body].length,
                max: VIBER_BODY_MAX_CHARS,
              })}
            </Typography>
          </Box>
          <Popover
            open={Boolean(viberEmojiAnchor)}
            anchorEl={viberEmojiAnchor}
            onClose={() => setViberEmojiAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            onMouseDown={(e) => e.stopPropagation()}
            PaperProps={{
              sx: { p: 1, maxWidth: 280, zIndex: 25000 },
              className: 'nodrag nopan',
            }}
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }} className="nodrag nopan">
              {VIBER_EMOJI_GRID.map((emoji) => (
                <Button
                  key={emoji}
                  size="small"
                  variant="text"
                  sx={{ minWidth: 36, fontSize: '1.25rem', lineHeight: 1 }}
                  className="nodrag nopan"
                  onClick={() => {
                    patch({ body: d.body + emoji });
                    setViberEmojiAnchor(null);
                  }}
                >
                  {emoji}
                </Button>
              ))}
            </Box>
          </Popover>
        </>
      )}
      {d.channel === 'waba' && (
        <>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mt: -0.25 }}>
            {t('flowNodes.commonSendSection')}
          </Typography>
          <TextField
            size="small"
            fullWidth
            label={t('flowNodes.commonSenderLabel')}
            placeholder={t('flowNodes.wabaSenderPlaceholder')}
            value={d.senderName}
            onChange={(e) => patch({ senderName: e.target.value })}
            className="nodrag nopan"
          />
          <TextField
            size="small"
            fullWidth
            label={t('flowNodes.wabaTemplate')}
            value={d.templateName}
            onChange={(e) => patch({ templateName: e.target.value })}
            className="nodrag nopan"
          />
          <TextField
            size="small"
            fullWidth
            label={t('flowNodes.wabaLang')}
            value={d.languageCode}
            onChange={(e) => patch({ languageCode: e.target.value })}
            className="nodrag nopan"
          />
          <TextField
            size="small"
            fullWidth
            label={t('flowNodes.wabaBody')}
            multiline
            minRows={2}
            value={d.body}
            onChange={(e) => patch({ body: e.target.value })}
            className="nodrag nopan"
          />
        </>
      )}
      {showViberSmsForwardingSwitch && viberSmsCascadeCtx ? (
        <>
          <Divider sx={{ my: 0.25 }} />
          <FormControl
            size="small"
            fullWidth
            disabled={viberSmsCascadeCtx.readOnly}
            className="nodrag nopan"
          >
            <InputLabel id="flow-viber-sms-cascade-mode-label">{t('flowEditor.viberSmsCascadeModeLabel')}</InputLabel>
            <Select
              labelId="flow-viber-sms-cascade-mode-label"
              label={t('flowEditor.viberSmsCascadeModeLabel')}
              value={viberSmsCascadeCtx.viberSmsCascadeLinked ? 'linked' : 'separate'}
              onChange={(e) =>
                viberSmsCascadeCtx.setViberSmsCascadeLinked(e.target.value === 'linked')
              }
            >
              <MenuItem value="linked">{t('flowEditor.viberSmsCascadeSendUndelivered')}</MenuItem>
              <MenuItem value="separate">{t('flowEditor.viberSmsCascadeSendAll')}</MenuItem>
            </Select>
            <FormHelperText sx={{ mx: 0 }}>{t('flowEditor.viberSmsCascadeLinkedHint')}</FormHelperText>
          </FormControl>
        </>
      ) : null}
    </Stack>
  );

  return fieldStack;
});

export const MailingChannelNode = memo(function MailingChannelNode(props: NodeProps) {
  const { data, id, type } = props;
  const { t } = useTranslation();
  const d = normalizeChannelNodeData(data);
  const meta = CHANNEL_META[d.channel];
  const { isSmsLight } = useSmsPageDesign();
  const campaignSubscriberListIds = useMailingFlowCampaignSubscriberListIds();
  const allNodes = useNodes();
  const isEventChain = visualChainIsEventTrigger(allNodes);
  const startData = useMemo(() => {
    const start = allNodes.find((n) => n.type === 'mailingStart');
    return start ? normalizeStartNodeData(start.data) : null;
  }, [allNodes]);
  const readinessOpts: ChannelNodeReadinessOpts | undefined = useMemo(() => {
    const o: ChannelNodeReadinessOpts = { isEventChain };
    const trimmed = campaignSubscriberListIds?.map((x) => String(x).trim()).filter(Boolean) ?? [];
    if (trimmed.length) {
      o.subscriberContactListIds = trimmed;
      o.subscriberContactListId = trimmed[0] ?? null;
    }
    if (startData) {
      o.startScheduleDate = startData.scheduleDate ?? '';
      o.startScheduleTime = startData.scheduleTime ?? '';
    }
    return o;
  }, [campaignSubscriberListIds, startData, isEventChain]);
  const offsetLabel =
    isEventChain &&
    (d.channel === 'sms' || d.channel === 'viber' || d.channel === 'email') &&
    String(d.triggerOffsetDays ?? '').trim() !== ''
      ? t('flowNodes.triggerOffsetChip', { days: d.triggerOffsetDays })
      : null;
  const { getEdges } = useReactFlow();
  const ring = canvasNodeRingStatus({ id, type, data }, getEdges(), readinessOpts);

  return (
    <Box
      sx={(theme) => ({
        ...nodeShellSx(isSmsLight),
        ...flowNodeRingSx(theme, ring),
      })}
    >
      <Box sx={{ height: 4, bgcolor: meta.color }} />
      <Box sx={{ px: 1.5, py: 1.1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, lineHeight: 1.2 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: meta.color,
              flexShrink: 0,
            }}
          />
          <Typography variant="subtitle2" fontWeight={600} sx={{ lineHeight: 1.2, color: 'text.primary' }}>
            {t(`channels.${d.channel}`)}
          </Typography>
        </Box>
        {offsetLabel ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
            {offsetLabel}
          </Typography>
        ) : null}
      </Box>
      <Handle type="target" position={Position.Left} id="in" style={handleStyle} />
      <Handle type="source" position={Position.Right} id="out" style={handleStyle} />
    </Box>
  );
});

export const DelayNodeInspectorForm = memo(function DelayNodeInspectorForm({ id, data }: { id: string; data: unknown }) {
  const { t } = useTranslation();
  const { value } = normalizeDelayNodeData(data);
  const { setNodes } = useReactFlow();
  const [text, setText] = useState(() => String(value));

  useEffect(() => {
    setText(String(value));
  }, [value, id]);

  const patchDelay = useCallback(
    (nextRaw: number) => {
      const v = clampDelayMinutes(Math.floor(nextRaw));
      setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id !== id) return node;
          return {
            ...node,
            data: { value: v, unit: 'minutes' as DelayTimeUnit } as DelayNodeData,
          };
        })
      );
    },
    [id, setNodes]
  );

  return (
    <Stack spacing={1.25} className="nodrag nopan">
      <TextField
        className="nodrag nopan"
        size="small"
        fullWidth
        type="text"
        inputMode="numeric"
        label={t('flowNodes.delayDuration')}
        helperText={t('flowNodes.delayMinutesRange', { min: DELAY_MIN_MINUTES, max: DELAY_MAX_MINUTES })}
        value={text}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '') {
            setText('');
            return;
          }
          if (!/^\d+$/.test(raw)) return;
          setText(raw);
          patchDelay(parseInt(raw, 10));
        }}
        onBlur={() => {
          if (text === '') {
            setText(String(DELAY_MIN_MINUTES));
            patchDelay(DELAY_MIN_MINUTES);
            return;
          }
          patchDelay(parseInt(text, 10) || DELAY_MIN_MINUTES);
          setText(String(clampDelayMinutes(parseInt(text, 10) || DELAY_MIN_MINUTES)));
        }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
        {t('flowNodes.delayInspectorHint')}
      </Typography>
    </Stack>
  );
});

export const MailingDelayNode = memo(function MailingDelayNode(props: NodeProps) {
  const { t } = useTranslation();
  const { data, id, type } = props;
  const { value, unit } = normalizeDelayNodeData(data);
  const { isSmsLight } = useSmsPageDesign();
  const { getEdges } = useReactFlow();
  const ring = canvasNodeRingStatus({ id, type, data }, getEdges());

  return (
    <Box
      sx={(theme) => ({
        ...nodeShellSx(isSmsLight),
        ...flowNodeRingSx(theme, ring),
      })}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1.1,
          bgcolor: smsPageTokens(isSmsLight).card,
        }}
      >
        <Handle type="target" position={Position.Left} id="in" style={handleStyle} />
        <DelayIcon sx={{ fontSize: 20, color: 'secondary.main', flexShrink: 0 }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {t('flowNodes.delayCardTitle')}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {value} {t(`delayUnits.${unit}`)}
          </Typography>
        </Box>
        <Handle type="source" position={Position.Right} id="out" style={handleStyle} />
      </Box>
    </Box>
  );
});

export const NoteNodeInspectorForm = memo(function NoteNodeInspectorForm({ id, data }: { id: string; data: unknown }) {
  const { t } = useTranslation();
  const d: NoteNodeData =
    typeof data === 'object' && data !== null && 'text' in data
      ? { text: String((data as NoteNodeData).text ?? '') }
      : defaultNoteNodeData();
  const { setNodes } = useReactFlow();

  const patch = useCallback(
    (text: string) => {
      setNodes((nodes) =>
        nodes.map((node) => (node.id === id ? { ...node, data: { ...d, text } } : node))
      );
    },
    [d, id, setNodes]
  );

  return (
    <TextField
      className="nodrag nopan"
      size="small"
      fullWidth
      multiline
      minRows={6}
      placeholder={t('flowNodes.notePlaceholder')}
      value={d.text}
      onChange={(e) => patch(e.target.value)}
    />
  );
});

export const MailingNoteNode = memo(function MailingNoteNode(props: NodeProps) {
  const { t } = useTranslation();
  const { data } = props;
  const d: NoteNodeData =
    typeof data === 'object' && data !== null && 'text' in data
      ? { text: String((data as NoteNodeData).text ?? '') }
      : defaultNoteNodeData();
  const { isSmsLight } = useSmsPageDesign();
  const preview = d.text.replace(/\s+/g, ' ').trim().slice(0, 56);

  return (
    <Box
      sx={{
        minWidth: 200,
        maxWidth: 260,
        borderRadius: '8px',
        border: '1px dashed',
        borderColor: isSmsLight ? SMS_PAGE.outlineBorder : 'rgba(255, 213, 79, 0.55)',
        bgcolor: isSmsLight ? 'rgba(255, 248, 225, 0.95)' : 'rgba(255, 213, 79, 0.12)',
        p: 1.1,
        boxShadow: smsPageTokens(isSmsLight).shadow,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.35 }}>
        <NoteIcon sx={{ fontSize: 18, color: 'warning.dark' }} />
        <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
          {t('flowEditor.paletteNote')}
        </Typography>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.35, wordBreak: 'break-word' }}>
        {preview || t('flowEditor.nodeTapToConfigure')}
        {d.text.trim().length > 56 ? '…' : ''}
      </Typography>
    </Box>
  );
});
